import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiClient from '../utils/apiClient';
import { getCurrentUser } from '../utils/auth';
import { usePlan } from './PlanContext';

const BranchContext = createContext();

/**
 * Hook to access branch state.
 *
 * Returns {
 *   branches, selectedBranchId, selectBranch, loading, refetchBranches,
 *   isMultiBranch, restricted, canSelectAll, branchKey
 * }
 *
 * `branches` is only ever the branches the CALLER may open — the backend scopes
 * `GET /branches` to their entitlement — so the selector can be built straight
 * from it without a second permission check.
 *
 * `branchKey` is the value every branch-aware page must put in its fetch
 * dependency array. It exists because the `vc-branch-change` event this context
 * dispatches had no listeners anywhere in the app: pages re-fetched only if
 * they happened to depend on `selectedBranchId`, so switching branch left stale
 * data on every page that had not opted in.
 */
export const useBranch = () => {
  const context = useContext(BranchContext);
  if (!context) {
    throw new Error('useBranch must be used within BranchProvider');
  }
  return context;
};

/**
 * Convenience hook for list pages: the one value to drop into a deps array.
 *
 *   const branchKey = useBranchKey();
 *   useEffect(() => { fetchRows(); }, [branchKey, page]);
 */
export const useBranchKey = () => useBranch().branchKey;

export const BranchProvider = ({ children }) => {
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(
    () => localStorage.getItem('vc-selected-branch') || 'all'
  );
  const [restricted, setRestricted] = useState(false);
  const [loading, setLoading] = useState(true);
  const { hasPlanFeature } = usePlan();

  const isMultiBranch = hasPlanFeature('multiBranch');

  const fetchBranches = useCallback(async () => {
    try {
      const user = getCurrentUser();
      if (!user || user.usertype === 'tenantadmin' || user.usertype === 'student') {
        setBranches([]);
        setRestricted(false);
        setLoading(false);
        return;
      }

      if (!isMultiBranch) {
        setBranches([]);
        setRestricted(false);
        setLoading(false);
        return;
      }

      const { data } = await apiClient.get('/branches');
      const fetched = data?.branches || [];
      const isRestricted = !!data?.restricted;
      setBranches(fetched);
      setRestricted(isRestricted);

      const saved = localStorage.getItem('vc-selected-branch');
      const savedIsValid = saved && saved !== 'all' && fetched.some(b => b._id === saved);

      if (savedIsValid) return;

      // A restricted user has no "all branches" scope to fall back on, and a
      // single-branch user gains nothing from one — pin them to their own
      // branch so the header they send matches what the API would allow anyway.
      if (isRestricted || fetched.length === 1) {
        const own = fetched.find(b => b.isDefault) || fetched[0];
        if (own) {
          setSelectedBranchId(own._id);
          localStorage.setItem('vc-selected-branch', own._id);
        }
        return;
      }

      // Unrestricted user with a stale/absent selection → all branches.
      if (saved && !savedIsValid) {
        setSelectedBranchId('all');
        localStorage.removeItem('vc-selected-branch');
      }
    } catch (err) {
      console.warn('Failed to fetch branches:', err.message);
      setBranches([]);
      setRestricted(false);
    } finally {
      setLoading(false);
    }
  }, [isMultiBranch]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  // Listen for auth changes — refetch on login/logout.
  // Note this deliberately does NOT force 'all': fetchBranches re-derives the
  // right default for whoever just signed in, and defaulting to the widest
  // possible scope on every login was how a branch admin ended up unscoped.
  useEffect(() => {
    const handleAuth = () => {
      localStorage.removeItem('vc-selected-branch');
      setSelectedBranchId('all');
      fetchBranches();
    };
    window.addEventListener('vc-auth-change', handleAuth);
    return () => window.removeEventListener('vc-auth-change', handleAuth);
  }, [fetchBranches]);

  const selectBranch = useCallback((branchId) => {
    const val = branchId || 'all';
    setSelectedBranchId(val);
    if (val === 'all') {
      localStorage.removeItem('vc-selected-branch');
    } else {
      localStorage.setItem('vc-selected-branch', val);
    }
    // Kept for any page that prefers an event to a deps value.
    window.dispatchEvent(new Event('vc-branch-change'));
  }, []);

  // Only an unrestricted user with more than one branch has an "all" scope.
  const canSelectAll = !restricted && branches.length > 1;

  return (
    <BranchContext.Provider value={{
      branches,
      selectedBranchId,
      selectBranch,
      loading,
      refetchBranches: fetchBranches,
      isMultiBranch,
      restricted,
      canSelectAll,
      branchKey: isMultiBranch ? selectedBranchId : 'all',
    }}>
      {children}
    </BranchContext.Provider>
  );
};
